import { Image, Card, CardBody, Heading, Link, SimpleGrid, Text } from "@chakra-ui/react";
import LandingContent from "../../components/landing-content";
import { getPosts } from "../../server/get-posts";
import { siteConfig } from "../../configuration/config";

export const cmsBaseLink = "https://cms.aidia.dk/wp-json/wp/v2";

// Requires ?_embed parameter to be included in the request
export const getWpImage = (post: any, size: string) =>
  post._embedded?.["wp:featuredmedia"]?.[0]?.media_details?.sizes?.[size]?.source_url || "";

export const getWpCategories = (post: any) => {
  return post._embedded?.["wp:term"]?.[0]?.map((category: any) => category.name);
};

export async function fetchWordPressPosts() {
  // Don't fetch posts in development - slows down the dev server
  if (process.env.NODE_ENV === "development") {
    return [];
  }

  const res = await fetch(`${cmsBaseLink}/posts?_embed&per_page=100`);
  const posts = await res.json();

  return posts
    .filter((post: any) => getWpCategories(post).includes("talkio"))
    .map((post: any) => {
      // Extract the featured image URL from the embedded media object
      const featuredImage = getWpImage(post, "medium");

      return {
        params: {
          id: post.slug,
          title: post.title.rendered,
          image: featuredImage, // Use the extracted featured image URL
        },
        isWordPress: true,
      };
    });
}

interface PostMeta {
  params: {
    id: string;
    title: string;
    image: string;
  };
  isWordPress?: boolean;
}

export default function Blog({
  combinedPosts,
  genPosts,
}: {
  combinedPosts: PostMeta[];
  genPosts: PostMeta[];
}) {
  return (
    <LandingContent>
      <Heading mb={3} size="md" as="h1">
        Blog
      </Heading>

      <Text mb={10} as="h2" fontSize="xl">
        Stay updated with the latest trends in language training and AI
      </Text>

      <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
        {combinedPosts.map((post, i) => (
          <Card
            direction={{ base: "column" }}
            overflow="hidden"
            variant="outline"
            size="sm"
            key={i}
            as="a"
            href={post.isWordPress ? `/blog/${post.params.id}` : `/blog/posts/${post.params.id}`}
            alignItems={"center"}
            _hover={{ opacity: 0.8 }}
          >
            <Image
              objectFit="cover"
              w="100%"
              h="150px"
              mt={0}
              src={post.params.image}
              alt={post.params.title}
            />

            <CardBody data-id="card-body" pt={0}>
              <Heading size="2xs" p={2}>
                {post.params.title}
              </Heading>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>

      <Heading mt={10} mb={5} size="xs">
        More Articles
      </Heading>
      {genPosts.map((post, i) => (
        <Link
          key={i}
          href={`/blog/posts/${post.params.id}`}
          py={3}
          px={5}
          border="1px solid #ddd"
          display="block"
          bg="#fbfbfb"
          _hover={{ textDecoration: "none", bg: "brand.gray.200" }}
        >
          {post.params.title}
        </Link>
      ))}
    </LandingContent>
  );
}

// Fetch posts and static props
export async function getStaticProps() {
  const wordpressPosts = siteConfig.isDefault ? await fetchWordPressPosts() : [];
  const customPosts = siteConfig.isDefault ? getPosts("custom") : [];
  const genPosts = siteConfig.isDefault ? getPosts("gen") : [];

  // Combine WordPress and Custom posts into one array
  const combinedPosts = [...wordpressPosts, ...customPosts];

  return {
    props: {
      combinedPosts,
      genPosts,
    },
    revalidate: 60, // ISR: Revalidate every 60 seconds
  };
}
